const MAX_STRING_LENGTH = 120;
const MAX_KEYS = 10;

type CompactValue =
  | null
  | string
  | number
  | boolean
  | {
      type: 'array';
      length: number;
      sample?: CompactValue[];
    }
  | {
      type: 'object';
      keys: string[];
      values?: Record<string, CompactValue>;
    };

type HttpErrorContext = {
  status?: number;
  method?: string;
  url?: string;
  responseBody?: CompactValue | Record<string, CompactValue>;
};

interface MutationErrorLogContextInput {
  error: unknown;
  mutationKey: unknown;
  variables: unknown;
  willSkip: boolean;
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

const truncateString = (value: string): string => {
  if (value.length <= MAX_STRING_LENGTH) {
    return value;
  }

  return `${value.slice(0, MAX_STRING_LENGTH - 3)}...`;
};

const summarizeValue = (value: unknown, includeValues = false): CompactValue => {
  if (value === null || typeof value === 'boolean' || typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    return truncateString(value);
  }

  if (Array.isArray(value)) {
    return {
      type: 'array',
      length: value.length,
      sample: value.slice(0, 3).map((item) => summarizeValue(item)),
    };
  }

  if (isRecord(value)) {
    const keys = Object.keys(value).slice(0, MAX_KEYS);
    const summary: CompactValue = {
      type: 'object',
      keys,
    };

    if (includeValues) {
      summary.values = Object.fromEntries(keys.map((key) => [key, summarizeValue(value[key])]));
    }

    return summary;
  }

  return String(value);
};

const serializeResponseBody = (value: unknown): CompactValue | Record<string, CompactValue> => {
  if (!isRecord(value)) {
    return summarizeValue(value, true);
  }

  return Object.fromEntries(
    Object.keys(value)
      .slice(0, MAX_KEYS)
      .map((key) => [key, summarizeValue(value[key])]),
  );
};

const getErrorMessage = (error: unknown): string => {
  if (isRecord(error) && typeof error.message === 'string') {
    return error.message;
  }

  return String(error);
};

const getHttpErrorContext = (error: unknown): HttpErrorContext | undefined => {
  if (!isRecord(error)) {
    return undefined;
  }

  const config = isRecord(error.config) ? error.config : undefined;
  const response = isRecord(error.response) ? error.response : undefined;
  const status = typeof response?.status === 'number' ? response.status : undefined;
  const method = typeof config?.method === 'string' ? config.method.toUpperCase() : undefined;
  const url = typeof config?.url === 'string' ? config.url : undefined;
  const responseBody = response && 'data' in response ? serializeResponseBody(response.data) : undefined;

  if (!status && !method && !url && !responseBody) {
    return undefined;
  }

  return {
    status,
    method,
    url,
    responseBody,
  };
};

export const makeMutationErrorLogContext = ({ error, mutationKey, variables, willSkip }: MutationErrorLogContextInput) => {
  return {
    source: 'TANSTACK_MUTATION_ERROR',
    mutationKey,
    willSkip,
    errorMessage: getErrorMessage(error),
    http: getHttpErrorContext(error),
    variables: summarizeValue(variables, true),
  };
};
